/**
 * Yapılandırma denetimi — "neyim bozuk?" sorusunun TEK ekrandaki cevabı.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ÜÇ TASARIM KARARI
 *
 * 1. HER BULGUNUN DÜZELTME YOLU VAR. Sorunu gösterip kullanıcıyı listelerde
 *    aramaya bırakmak, sorunu göstermemekten biraz iyidir — ama az. Her satır
 *    ilgili kaydın düzenleme/detay ekranına bağlanıyor.
 * 2. HER BULGU "NEDEN ÖNEMLİ" SÖYLÜYOR. "Panosuz saha" bir gözlemdir;
 *    "o sahadan veri gelemez" bir sonuçtur. Kullanıcı sonucu görmeden
 *    önceliklendiremez.
 * 3. TEMİZ SONUÇ TARANANI GÖSTERİR. Boş liste iki şey demek olabilir: her şey
 *    yolunda, ya da ekran bozuk. Kaç kayıt tarandığını yazmak ikisini ayırır.
 * ═══════════════════════════════════════════════════════════════════════════
 */
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { DenetimKurali, DenetimOnemi } from '@gasco/shared';

import { Badge, type Tone } from '../design/Status.tsx';
import { Button } from '../design/Button.tsx';
import { Card, CardBody } from '../design/Card.tsx';
import { EmptyState, SkeletonList } from '../design/Feedback.tsx';
import { KapsamNotu, YapilandirmaSekmeleri } from './parts.tsx';
import { PageBody, PageHeader } from '../shell/AppShell.tsx';
import { istek } from '../lib/api.ts';

interface Bulgu {
  readonly rule: DenetimKurali;
  readonly severity: DenetimOnemi;
  readonly site_id: number | null;
  readonly site_name: string | null;
  readonly panel_id: number | null;
  readonly panel_serial: string | null;
  readonly detail: string | null;
}

interface DenetimCevabi {
  readonly findings: readonly Bulgu[];
  readonly scanned: { readonly sites: number; readonly panels: number; readonly tanks: number };
}

/**
 * Kural → başlık ve SONUÇ.
 *
 * `Record<DenetimKurali, …>` olduğu için sözleşmeye yeni bir kural eklenip
 * metni yazılmazsa DERLEME HATASI olur — ekranda ham kod görünmez.
 */
const METIN: Record<DenetimKurali, { readonly baslik: string; readonly sonuc: string }> = {
  site_without_panel: {
    baslik: 'Sahanın panosu yok',
    sonuc: 'Bu sahadan hiç veri gelemez — pano tanımlanmadan toplama başlamaz.',
  },
  site_polling_disabled: {
    baslik: 'Sahanın tüm panolarında yoklama kapalı',
    sonuc: 'Sistem bu sahadan veri beklemiyor; ekranlarda sessiz görünecek.',
  },
  panel_polling_disabled: {
    baslik: 'Panonun yoklaması kapalı',
    sonuc: 'Sahanın diğer panoları okunuyor, bu pano okunmuyor.',
  },
  site_without_tank: {
    baslik: 'Sahada tank tanımlı değil',
    sonuc: 'Panodan gelen tank ölçümleri hiçbir tanka bağlanamaz; veri toplanır ama kullanılamaz.',
  },
  duplicate_plc_address: {
    baslik: 'İki pano aynı adrese bağlanıyor',
    sonuc: 'En az biri yanlış yapılandırılmış — biri diğerinin verisini gösteriyor olabilir.',
  },
  suspicious_location: {
    baslik: 'Konum girilmemiş görünüyor (0, 0)',
    sonuc: 'Saha haritada Afrika’nın batısında çıkar; saha ekibi yanlış yere yönlendirilebilir.',
  },
  unverified_profile: {
    baslik: 'PLC profilinde doğrulanmamış register var',
    // Pano başına değil PROFİL başına: aynı uyarıyı 350 kez göstermek,
    // gerçek bulguları gürültünün içinde kaybederdi.
    sonuc: 'Bu sinyaller gerçek cihazda teyit edilmedi; alarm bağlanmadan önce doğrulanmalı.',
  },
};

/** Önem → rozet tonu. Gri = bilgi; her eksiklik alarm değildir. */
const TON: Record<DenetimOnemi, Tone> = {
  danger: 'danger',
  warn: 'warn',
  info: 'neutral',
};

const ONEM_ETIKETI: Record<DenetimOnemi, string> = {
  danger: 'Hatalı',
  warn: 'Eksik',
  info: 'Bilgi',
};

export function Denetim(): React.JSX.Element {
  const sorgu = useQuery({
    queryKey: ['denetim'],
    queryFn: () => istek<DenetimCevabi>('/config-audit'),
  });

  return (
    <>
      <PageHeader
        title="Denetim"
        description="Veri toplanmasını engelleyen yapılandırma eksikleri"
      />
      <YapilandirmaSekmeleri />
      <KapsamNotu />
      <PageBody>
        {sorgu.isPending && <SkeletonList rows={3} />}

        {sorgu.isError && (
          <EmptyState
            title="Denetim çalıştırılamadı"
            description={
              sorgu.error instanceof Error ? sorgu.error.message : 'Bilinmeyen bir hata.'
            }
            action={<Button onClick={() => void sorgu.refetch()}>Tekrar dene</Button>}
          />
        )}

        {sorgu.data !== undefined && (
          <div className="flex flex-col gap-4">
            <Ozet cevap={sorgu.data} />
            {sorgu.data.findings.length > 0 && (
              <ul className="flex flex-col gap-3">
                {sorgu.data.findings.map((b, i) => (
                  <li key={`${b.rule}-${String(b.site_id)}-${String(b.panel_id)}-${String(i)}`}>
                    <BulguKarti bulgu={b} />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </PageBody>
    </>
  );
}

/**
 * Özet — bulgu VARKEN de gösteriliyor.
 *
 * "3 bulgu" tek başına ölçek vermez: 3/4 saha bozuksa ile 3/400 bozuksa aynı
 * cümle çok farklı iki durumu anlatır.
 */
function Ozet({ cevap }: { readonly cevap: DenetimCevabi }): React.JSX.Element {
  const { findings, scanned } = cevap;
  const tarandi = `${scanned.sites} saha · ${scanned.panels} pano · ${scanned.tanks} tank tarandı`;

  if (findings.length === 0) {
    return (
      <EmptyState
        title="Yapılandırmada eksik bulunmadı"
        // Boş sonuç "tarama çalışmadı" ile karışmasın.
        description={tarandi}
      />
    );
  }

  const hatali = findings.filter((f) => f.severity === 'danger').length;
  const eksik = findings.filter((f) => f.severity === 'warn').length;
  const bilgi = findings.filter((f) => f.severity === 'info').length;

  return (
    <div
      role="status"
      className="rounded-[var(--radius-card)] border border-border bg-surface p-3 text-sm"
    >
      <p className="font-medium text-ink">
        {findings.length} bulgu — {hatali} hatalı · {eksik} eksik · {bilgi} bilgi
      </p>
      <p className="mt-0.5 text-ink-muted">{tarandi}</p>
    </div>
  );
}

function BulguKarti({ bulgu }: { readonly bulgu: Bulgu }): React.JSX.Element {
  const metin = METIN[bulgu.rule];

  return (
    <Card>
      <CardBody>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="font-medium text-ink">{metin.baslik}</p>
            <p className="mt-0.5 text-sm text-ink-muted">{metin.sonuc}</p>
          </div>
          <Badge tone={TON[bulgu.severity]}>{ONEM_ETIKETI[bulgu.severity]}</Badge>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
          {bulgu.site_name !== null && (
            <span className="text-ink-muted">
              Saha: <span className="font-medium text-ink">{bulgu.site_name}</span>
            </span>
          )}
          {bulgu.panel_serial !== null && (
            <span className="text-ink-muted">
              Pano: <span className="font-medium text-ink">{bulgu.panel_serial}</span>
            </span>
          )}
          <Ek bulgu={bulgu} />
        </div>

        {/* DÜZELTME YOLU — bulguyu gösterip kullanıcıyı aramaya bırakmıyoruz. */}
        <div className="mt-3">
          <Duzeltme bulgu={bulgu} />
        </div>
      </CardBody>
    </Card>
  );
}

/** Kurala göre anlamı değişen `detail` alanı — ham sayı tek başına okunmaz. */
function Ek({ bulgu }: { readonly bulgu: Bulgu }): React.JSX.Element | null {
  if (bulgu.detail === null || bulgu.detail === '') return null;

  const metin =
    bulgu.rule === 'duplicate_plc_address'
      ? `Adres: ${bulgu.detail}`
      : bulgu.rule === 'site_polling_disabled'
        ? `${bulgu.detail} pano, hepsi kapalı`
        : bulgu.rule === 'unverified_profile'
          ? // Sunucu bunu PROFİL BAŞINA topluyor; cümle olarak geliyor.
            bulgu.detail
          : bulgu.rule === 'site_without_panel'
            ? `Tesisat kodu: ${bulgu.detail}`
            : bulgu.detail;

  return <span className="text-ink-muted">{metin}</span>;
}

/**
 * Bulgudan DOĞRU ekrana gider.
 *
 * Pano ile ilgili bulgu panonun düzenleme formuna, saha ile ilgili olan
 * sahanın detayına — çünkü "pano ekle" ve "tank ekle" oradan çıkıyor.
 * Konum hatası doğrudan saha formuna: düzeltilecek alan orada.
 */
function Duzeltme({ bulgu }: { readonly bulgu: Bulgu }): React.JSX.Element | null {
  if (bulgu.panel_id !== null) {
    return (
      <Link
        to="/panolar/$panoId/duzenle"
        params={{ panoId: String(bulgu.panel_id) }}
        className="text-sm font-medium text-brand-700"
      >
        Panoyu düzenle
      </Link>
    );
  }

  if (bulgu.site_id === null) return null;

  if (bulgu.rule === 'suspicious_location') {
    return (
      <Link
        to="/sahalar/$sahaId/duzenle"
        params={{ sahaId: String(bulgu.site_id) }}
        className="text-sm font-medium text-brand-700"
      >
        Konumu düzelt
      </Link>
    );
  }

  return (
    <Link
      to="/sahalar/$sahaId"
      params={{ sahaId: String(bulgu.site_id) }}
      className="text-sm font-medium text-brand-700"
    >
      Sahayı aç
    </Link>
  );
}
